import type { AeroSearchSpaceEstimate, AeroSearchSpaceTier } from '@/features/tradeLogicAuthoring/components/aeroOptimizationJobInput';

interface SearchSpaceSummaryProps {
    searchSpace: AeroSearchSpaceEstimate;
}

const formatCount = (value: number): string => new Intl.NumberFormat().format(value);
const formatNullableCount = (value: number | null, fallback: string): string => (value === null ? fallback : formatCount(value));

const TIER_STYLES: Record<AeroSearchSpaceTier, string> = {
    empty: 'border-zinc-700 bg-zinc-800/60 text-zinc-400',
    quick: 'border-green-800 bg-green-900/30 text-green-300',
    standard: 'border-cyan-800 bg-cyan-900/30 text-cyan-300',
    large: 'border-amber-800 bg-amber-900/30 text-amber-200',
    huge: 'border-red-800 bg-red-900/40 text-red-200'
};

const SearchSpaceSummary = ({ searchSpace }: SearchSpaceSummaryProps) => {
    const { families, combinations, entityCount, simulations, tier, tierLabel } = searchSpace;

    return (
        <div>
            <div className="space-y-1.5">
                {families.length === 0 ? (
                    <div className="text-sm text-zinc-500">Enable an exit type to size the search.</div>
                ) : (
                    families.map((family) => (
                        <div className="flex items-baseline justify-between gap-3 text-sm" key={family.key}>
                            <div className="min-w-0">
                                <span className="text-zinc-300">{family.label}</span>
                                {family.detail && <span className="ml-2 text-xs text-zinc-500">{family.detail}</span>}
                            </div>
                            <span className="shrink-0 font-medium text-zinc-200">
                                {formatCount(family.variants)} variant{family.variants === 1 ? '' : 's'}
                            </span>
                        </div>
                    ))
                )}
            </div>

            <div className="my-3 border-t border-zinc-800" />

            <div className="space-y-1.5">
                <div className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="text-zinc-400">Exit combinations</span>
                    <span className="font-semibold text-zinc-100">{formatCount(combinations)}</span>
                </div>
                <div className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="text-zinc-400">Entities</span>
                    <span className="font-medium text-zinc-300">
                        {entityCount === null ? 'screener-resolved' : `× ${formatCount(entityCount)}`}
                    </span>
                </div>
                <div className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="text-zinc-400">Total simulations</span>
                    <span className="font-semibold text-zinc-100">{formatNullableCount(simulations, 'Resolved at run time')}</span>
                </div>
            </div>

            <div className={`mt-3 rounded-md border px-3 py-2 text-xs font-medium ${TIER_STYLES[tier]}`}>{tierLabel}</div>
        </div>
    );
};

export default SearchSpaceSummary;
